import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Receipt, Download, CheckCircle2, XCircle, Loader2, FileText } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ST-event invoice + sous-traitance info block.
 *
 * - Shows sous-traitant, HT, TTC, volume, AOT flags.
 * - Generate / reuse invoice (FAC-ST-YYYY-XXXX).
 * - Paid / Unpaid toggle.
 * - Download invoice PDF.
 *
 * Props:
 *   eventId : string
 *   detailData : the event detail object from /agenda-demenagement/event/{id}
 */
export default function STFactureSection({ eventId, detailData }) {
    const [facture, setFacture] = useState(null);
    const [busy, setBusy] = useState(false);

    const fetchFacture = useCallback(async () => {
        try {
            const res = await axios.get(
                `${API_URL}/api/sous-traitance/events/${eventId}/facture`,
                { withCredentials: true }
            );
            setFacture(res.data);
        } catch {
            setFacture(null);
        }
    }, [eventId]);

    useEffect(() => { fetchFacture(); }, [fetchFacture]);

    const handleGenerate = async () => {
        setBusy(true);
        try {
            const res = await axios.post(
                `${API_URL}/api/sous-traitance/events/${eventId}/generate-facture`,
                {},
                { withCredentials: true }
            );
            await fetchFacture();
            toast.success(res.data.reused ? `Facture existante ${res.data.numero}` : `Facture créée ${res.data.numero}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur génération facture');
        } finally {
            setBusy(false);
        }
    };

    const handleTogglePaid = async () => {
        if (!facture) return;
        setBusy(true);
        try {
            await axios.put(
                `${API_URL}/api/sous-traitance/factures/${facture.id}/toggle-paid`,
                {},
                { withCredentials: true }
            );
            await fetchFacture();
        } catch {
            toast.error('Erreur mise à jour paiement');
        } finally {
            setBusy(false);
        }
    };

    const handleDownloadPDF = async () => {
        if (!facture) return;
        toast.loading('Génération PDF…', { id: 'st-fac-pdf' });
        try {
            const res = await axios.get(`${API_URL}/api/factures/${facture.id}/pdf`, {
                responseType: 'blob',
                withCredentials: true,
            });
            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `${facture.numero}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 500);
            toast.success('PDF téléchargé', { id: 'st-fac-pdf' });
        } catch {
            toast.error('Erreur PDF', { id: 'st-fac-pdf' });
        }
    };

    return (
        <div className="border-l-2 border-l-orange-500 pl-4 py-3 bg-orange-50/60 rounded-r-lg space-y-3" data-testid="st-facture-section">
            <span className="text-xs font-bold text-orange-700 uppercase">Sous-traitance</span>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                <div className="min-w-0">
                    <span className="text-xs text-gray-400">Sous-traitant</span>
                    <p className="font-semibold text-gray-800 truncate" title={detailData.sous_traitant_nom || '—'}>{detailData.sous_traitant_nom || '—'}</p>
                </div>
                <div>
                    <span className="text-xs text-gray-400">Prix HT</span>
                    <p className="font-semibold text-gray-800">{(detailData.prix_ht || 0).toFixed(2)} €</p>
                </div>
                <div>
                    <span className="text-xs text-gray-400">Prix TTC</span>
                    <p className="font-semibold text-gray-800">{(detailData.prix_ttc || 0).toFixed(2)} €</p>
                </div>
                {detailData.volume > 0 && (
                    <div>
                        <span className="text-xs text-gray-400">Volume</span>
                        <p className="font-semibold text-gray-800">{detailData.volume} m³</p>
                    </div>
                )}
                {detailData.aot_depart === 'oui' && (
                    <div>
                        <span className="text-xs text-gray-400">AOT départ</span>
                        <p className="font-semibold text-orange-700">Oui</p>
                    </div>
                )}
                {detailData.aot_arrivee === 'oui' && (
                    <div>
                        <span className="text-xs text-gray-400">AOT arrivée</span>
                        <p className="font-semibold text-orange-700">Oui</p>
                    </div>
                )}
            </div>

            {/* Facture row */}
            <div className="flex items-center flex-wrap gap-2 pt-2 border-t border-orange-200">
                <Receipt className="w-4 h-4 text-orange-700" />
                {!facture ? (
                    <>
                        <span className="text-xs text-gray-600 mr-2">Aucune facture émise</span>
                        <Button
                            size="sm"
                            onClick={handleGenerate}
                            disabled={busy || !(detailData.prix_ttc > 0)}
                            className="bg-orange-700 hover:bg-orange-800 text-white text-xs h-7 gap-1"
                            data-testid="st-generate-facture-btn"
                            title={detailData.prix_ttc > 0 ? '' : 'Renseignez d\'abord un prix TTC dans la fiche ST'}
                        >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                            Générer facture
                        </Button>
                    </>
                ) : (
                    <>
                        <span className="text-xs font-mono font-bold text-gray-800">{facture.numero}</span>
                        <span className="text-xs text-gray-500">{facture.prix_ttc.toFixed(2)} €</span>
                        {facture.paye ? (
                            <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200"
                                data-testid="st-facture-paid-badge"
                            >
                                <CheckCircle2 className="w-3 h-3" /> Payée
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 ring-1 ring-red-200">
                                <XCircle className="w-3 h-3" /> Non payée
                            </span>
                        )}
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleTogglePaid}
                            disabled={busy}
                            className="text-xs h-7 gap-1"
                            data-testid="st-toggle-paid-btn"
                        >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                            {facture.paye ? 'Marquer non payée' : 'Marquer payée'}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleDownloadPDF}
                            className="text-xs h-7 gap-1"
                            data-testid="st-facture-pdf-btn"
                        >
                            <Download className="w-3 h-3" /> PDF
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}
